import React, { useEffect, useState } from 'react';
import {
  Alert,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { goBackOrHome } from '../../navigation/goBackOrHome';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { MaterialIcons } from '@expo/vector-icons';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  updateEmail,
  clearError,
  selectUser,
  selectIsUpdatingProfile,
  selectAuthError,
  selectValidationErrors,
} from './authSlice';
import {
  BrandLogo,
  IconTextField,
  PrimaryCTA,
} from '../../components';
import { fpColors } from '../../styles/theme';
import type { UpdateEmailRequest } from '../../api/types';

const schema = yup.object({
  new_email: yup
    .string()
    .email('Please enter a valid email address')
    .required('New email is required'),
  password: yup
    .string()
    .required('Current password is required for security'),
});

/**
 * Change email — Fidelity Purple. Custom top bar, read-only current
 * email card, new-email + password fields, info callout, primary
 * CTA. Native stack header disabled.
 */
export const ChangeEmailScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();

  const user = useAppSelector(selectUser);
  const isLoading = useAppSelector(selectIsUpdatingProfile);
  const error = useAppSelector(selectAuthError);
  const validationErrors = useAppSelector(selectValidationErrors);

  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<UpdateEmailRequest>({
    resolver: yupResolver(schema),
    defaultValues: { new_email: '', password: '' },
  });

  const newEmail = watch('new_email');

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const onSubmit = async (data: UpdateEmailRequest) => {
    if (data.new_email === user?.email) {
      Alert.alert('Same email', 'That is already your current email.');
      return;
    }
    const result = await dispatch(updateEmail(data));
    if (updateEmail.fulfilled.match(result)) {
      Alert.alert(
        'Verification sent',
        `We\u2019ve sent a verification code to ${data.new_email}. Check your inbox and verify to complete the change.`,
        [
          {
            text: 'Continue',
            onPress: () => {
              navigation.navigate('EmailVerification', {
                type: 'email_change',
                email: data.new_email,
              });
            },
          },
        ],
      );
    }
  };

  const bannerError =
    (validationErrors && typeof validationErrors === 'string'
      ? validationErrors
      : null) ||
    (typeof error === 'string' ? error : null);

  const disabled = !newEmail || newEmail === user?.email;

  return (
    <View style={styles.page}>
      <View style={styles.topBar}>
        <View style={styles.topBarInner}>
          <Pressable
            onPress={() => goBackOrHome(navigation)}
            hitSlop={8}
            style={styles.topBtn}
            accessibilityRole="button"
            accessibilityLabel="Back"
          >
            <MaterialIcons name="arrow-back" size={22} color={fpColors.onSurface} />
          </Pressable>
          <View style={styles.topBrand}>
            <BrandLogo variant="horizontal" height={22} tone="color" />
          </View>
          <View style={styles.topBtn} />
        </View>
      </View>

      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={styles.scroll}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={styles.kicker}>ACCOUNT</Text>
          <Text style={styles.h1}>Change email</Text>
          <Text style={styles.subtitle}>
            We\u2019ll send a verification code to your new address.
          </Text>

          <View style={styles.currentCard}>
            <View style={styles.currentIcon}>
              <MaterialIcons name="mail" size={18} color={fpColors.primary} />
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.currentKicker}>Current email</Text>
              <Text style={styles.currentValue} numberOfLines={1}>
                {user?.email || 'Not set'}
              </Text>
            </View>
          </View>

          <View style={styles.card}>
            <Controller
              control={control}
              name="new_email"
              render={({ field: { onChange, onBlur, value } }) => (
                <IconTextField
                  label="New email"
                  leftIcon="alternate-email"
                  placeholder="name@example.com"
                  value={value || ''}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  error={errors.new_email?.message}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoFocus
                />
              )}
            />

            <Controller
              control={control}
              name="password"
              render={({ field: { onChange, onBlur, value } }) => (
                <IconTextField
                  label="Current password"
                  leftIcon="lock-outline"
                  rightIcon={showPassword ? 'visibility' : 'visibility-off'}
                  onRightIconPress={() => setShowPassword((v) => !v)}
                  placeholder="Enter your current password"
                  value={value || ''}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  error={errors.password?.message}
                  secureTextEntry={!showPassword}
                  autoComplete="current-password"
                />
              )}
            />
          </View>

          <View style={styles.infoCard}>
            <MaterialIcons
              name="info-outline"
              size={16}
              color={fpColors.primary}
            />
            <View style={{ flex: 1 }}>
              <Text style={styles.infoLine}>
                You\u2019ll receive a verification code at the new
                address.
              </Text>
              <Text style={styles.infoLine}>
                Your email won\u2019t change until you verify.
              </Text>
              <Text style={styles.infoLine}>
                You can keep using your current email in the meantime.
              </Text>
            </View>
          </View>

          {bannerError ? (
            <View style={styles.errorBanner}>
              <MaterialIcons
                name="error-outline"
                size={16}
                color={fpColors.error}
              />
              <Text style={styles.errorText}>{bannerError}</Text>
            </View>
          ) : null}

          <PrimaryCTA
            label={isLoading ? 'Sending\u2026' : 'Send verification email'}
            onPress={handleSubmit(onSubmit)}
            loading={isLoading}
            disabled={disabled}
            iconRight={isLoading ? null : 'send'}
          />

          <Text style={styles.securityNote}>
            We require your current password to change your email.
          </Text>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
  },
  topBarInner: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    gap: 10,
  },
  topBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  topBrand: { flex: 1, alignItems: 'center' },

  scroll: { padding: 20, gap: 14, paddingBottom: 30 },
  kicker: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.primary,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  h1: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 26,
    color: fpColors.onSurface,
    letterSpacing: -0.4,
    marginTop: 2,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    lineHeight: 18,
    color: fpColors.onSurfaceVariant,
    marginBottom: 6,
  },

  currentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    borderRadius: 14,
    backgroundColor: `${fpColors.primary}0a`,
    borderWidth: 1,
    borderColor: `${fpColors.primary}1f`,
  },
  currentIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    backgroundColor: fpColors.surfaceContainerLowest,
    justifyContent: 'center',
    alignItems: 'center',
  },
  currentKicker: {
    fontFamily: 'Inter_500Medium',
    fontSize: 10,
    color: fpColors.primary,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  currentValue: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
    marginTop: 1,
  },

  card: {
    padding: 14,
    borderRadius: 16,
    gap: 12,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },

  infoCard: {
    flexDirection: 'row',
    gap: 10,
    padding: 12,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  infoLine: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 18,
    color: fpColors.onSurfaceVariant,
  },

  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 10,
    backgroundColor: fpColors.errorContainer,
  },
  errorText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onErrorContainer,
  },

  securityNote: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.outline,
    textAlign: 'center',
    marginTop: 2,
  },
});
